import React, { Suspense, lazy, useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { TelegramProvider } from './contexts/TelegramContext';
import { AuthProvider, useAuth, onboardingKeyForUid } from './contexts/AuthContext';
import { EnergyProvider } from './contexts/EnergyContext';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import { captureReferral, getStoredReferrer, applyReferralReward } from './lib/referral';

const Onboarding = lazy(() => import('./components/Onboarding'));
const Login = lazy(() => import('./pages/Login'));
const Paywall = lazy(() => import('./pages/Paywall'));

const Home = lazy(() => import('./pages/Home'));
const Workout = lazy(() => import('./pages/Workout'));
const Profile = lazy(() => import('./pages/Profile'));
const Shop = lazy(() => import('./pages/Shop'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const Syndicates = lazy(() => import('./pages/Syndicates'));
const Biohacks = lazy(() => import('./pages/Biohacks'));
const Avatar = lazy(() => import('./pages/Avatar'));
const Coach = lazy(() => import('./pages/Coach'));
const Settings = lazy(() => import('./pages/Settings'));
const Oracle = lazy(() => import('./pages/Oracle'));
const Calendar = lazy(() => import('./pages/Calendar'));
const Memory = lazy(() => import('./pages/Memory'));
const Progress = lazy(() => import('./pages/Progress'));

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-[#0A0A0F]">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-2 border-[#00E5CC] border-t-transparent rounded-full animate-spin" />
        <span className="text-[#00E5CC] text-sm font-display tracking-[3px] uppercase">
          Loading...
        </span>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Read the USER-SCOPED onboarding-completed flag (#13). Falls back to the legacy
// global flag ONLY when no uid is known yet, and never lets the legacy flag make
// a *different* user skip onboarding (that decision is re-derived once uid is
// known). Returns true when onboarding should still be shown.
function shouldShowOnboarding(uid) {
  try {
    const authData = JSON.parse(localStorage.getItem('omnisio_workout_auth') || '{}');
    if (authData.authMethod === 'longevity_id' && authData.skipOnboarding) return false;
  } catch {}
  const key = onboardingKeyForUid(uid);
  if (key) {
    // uid known → this user's own flag is the single source of truth.
    return localStorage.getItem(key) !== 'true';
  }
  // uid not resolved yet → fall back to legacy global flag.
  return !localStorage.getItem('omnisio_workout_onboarding_done');
}

function AppContent() {
  const { isAuthenticated, loading, profile, uid, updateProfile } = useAuth();
  // Onboarding/paywall visibility is DERIVED from auth+uid, recomputed whenever
  // the resolved user changes — so a new account always sees onboarding and a
  // returning account skips it, with no carry-over from a previously signed-in
  // user (the prior global flag bug).
  const [showOnboarding, setShowOnboarding] = useState(() => shouldShowOnboarding(uid));
  const [showPaywall, setShowPaywall] = useState(() => {
    try {
      const authData = JSON.parse(localStorage.getItem('omnisio_workout_auth') || '{}');
      if (authData.authMethod === 'longevity_id' && authData.skipOnboarding) return false;
    } catch {}
    return !localStorage.getItem('omnisio_workout_subscription_active');
  });

  // Re-derive onboarding/paywall visibility once auth + uid are resolved. Gated
  // on !loading so we never decide off a stale/half-resolved cache (#13 race).
  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) return;
    setShowOnboarding(shouldShowOnboarding(uid));
    try {
      setShowPaywall(localStorage.getItem('omnisio_workout_subscription_active') !== 'true');
    } catch {
      setShowPaywall(true);
    }
  }, [loading, isAuthenticated, uid]);

  const handleOnboardingComplete = () => {
    // Persist BOTH the user-scoped flag (authoritative) and the legacy global
    // flag (back-compat for any other reader) so this user skips onboarding next
    // time without affecting other accounts.
    const key = onboardingKeyForUid(uid);
    if (key) localStorage.setItem(key, 'true');
    localStorage.setItem('omnisio_workout_onboarding_done', 'true');
    setShowOnboarding(false);

    // #11 name consistency: Onboarding wrote the chosen name only to the
    // persisted cache. Push it into the in-memory profile (single source of
    // truth) so Home and Profile show the SAME current-user name immediately —
    // no reload, no stale-cache divergence.
    try {
      const obProfile = JSON.parse(localStorage.getItem('omnisio_workout_profile') || '{}');
      const chosenName = obProfile?.display_name || obProfile?.first_name;
      if (chosenName && typeof updateProfile === 'function') {
        updateProfile({
          first_name: chosenName,
          display_name: obProfile?.display_name || chosenName,
        });
      }
    } catch { /* non-critical */ }

    // Auto-apply referral reward if a referrer was captured before signup.
    // profile.public_id may not be set yet if Supabase hasn't responded;
    // we pass it only for self-referral detection (null is safe — check skips).
    const pendingReferrer = getStoredReferrer();
    if (pendingReferrer) {
      const currentPublicId = profile?.public_id || null;
      const result = applyReferralReward(pendingReferrer, currentPublicId);
      if (result.success) {
        console.log('[REFERRAL] Onboarding reward applied, premium until:', result.premiumUntil);
      } else {
        console.log('[REFERRAL] Reward not applied:', result.reason);
      }
    }
  };

  const handlePaywallComplete = () => {
    localStorage.setItem('omnisio_workout_subscription_active', 'true');
    setShowPaywall(false);
  };

  // Still resolving auth state — DO NOT decide landing-vs-home until auth is
  // fully resolved (#13). When authenticated, also wait for the profile object
  // so onboarding/name decisions derive from real data, not an empty cache.
  if (loading || (isAuthenticated && !profile)) {
    return <LoadingFallback />;
  }

  // Not logged in -> show login
  if (!isAuthenticated) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    );
  }

  // Logged in but onboarding not done
  if (showOnboarding) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <Onboarding onComplete={handleOnboardingComplete} />
      </Suspense>
    );
  }

  // Logged in but no subscription
  if (showPaywall) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <Paywall onComplete={handlePaywallComplete} />
      </Suspense>
    );
  }

  return (
    <Layout>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/workout" element={<Workout />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          {/* Canonical routes: /teams + /challenges (Home navigates here).
              /syndicates + /biohacks kept as aliases for any legacy deep links. */}
          <Route path="/teams" element={<Syndicates />} />
          <Route path="/challenges" element={<Biohacks />} />
          <Route path="/syndicates" element={<Navigate to="/teams" replace />} />
          <Route path="/biohacks" element={<Navigate to="/challenges" replace />} />
          <Route path="/avatar" element={<Avatar />} />
          {/* Radio deprecated (May 2026) — YouTube embed, not core to fitness loop */}
          <Route path="/radio" element={<Navigate to="/" replace />} />
          <Route path="/coach" element={<Coach />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/oracle" element={<Oracle />} />
          <Route path="/calendar" element={<Calendar />} />
          <Route path="/memory" element={<Memory />} />
          <Route path="/progress" element={<Progress />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Layout>
  );
}

export default function App() {
  // ─── Capture referral code from URL BEFORE auth resolves ────────────
  // Must run at the App level (outside AuthProvider) so it fires
  // immediately on any page load, including /workout/join?ref=OMW-...
  useEffect(() => {
    captureReferral(); // stores ?ref= to localStorage if valid; no-op otherwise
  }, []); // empty deps — run exactly once on mount

  return (
    <ErrorBoundary>
      <TelegramProvider>
        <AuthProvider>
          <EnergyProvider>
            <AppContent />
          </EnergyProvider>
        </AuthProvider>
      </TelegramProvider>
    </ErrorBoundary>
  );
}
